<template>
  <div
    class="ctc-offer-check"
    :class="[
      `ctc-offer-check--${status.key}`,
      { 'ctc-offer-check--compact': compact },
    ]"
    role="status"
  >
    <span class="ctc-offer-check__icon" aria-hidden="true">
      <v-progress-circular
        v-if="status.key === 'loading'"
        indeterminate
        :size="compact ? 14 : 18"
        :width="2"
        color="currentColor"
      />
      <v-icon v-else :size="compact ? 16 : 19" color="currentColor">
        {{ status.icon }}
      </v-icon>
    </span>

    <span class="ctc-offer-check__copy">
      <strong>{{ status.label }}</strong>
      <small v-if="!compact && status.detail">{{ status.detail }}</small>
    </span>

    <small v-if="!compact && site" class="ctc-offer-check__media">
      Média {{ site.affilizzMediaName }}
    </small>

    <button
      v-if="retryable && status.canRetry"
      type="button"
      class="ctc-offer-check__retry"
      @click="$emit('retry')"
    >
      Réessayer
    </button>
  </div>
</template>

<script>
import {
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiClockOutline,
  mdiCloseCircleOutline,
} from "@mdi/js";
import { computed } from "@vue/composition-api";

/** Read-only step-1 check of the main product offer on the site media. */
export default {
  name: "CtcOfferCheckStatus",
  props: {
    offerCheck: {
      type: Object,
      default: null,
    },
    checking: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: "",
    },
    site: {
      type: Object,
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    retryable: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const status = computed(() => {
      if (props.checking) {
        return {
          key: "loading",
          label: "Recherche des offres Affilizz…",
          detail: "Recherche d’une offre pour ce produit et ce site.",
          icon: mdiClockOutline,
        };
      }
      if (props.error) {
        return {
          key: "error",
          label: "Recherche Affilizz impossible",
          detail: props.error,
          icon: mdiCloseCircleOutline,
          canRetry: true,
        };
      }
      if (props.offerCheck && !props.offerCheck.hasOffer) {
        return {
          key: "warning",
          label: "Aucune offre Affilizz utilisable",
          detail:
            props.offerCheck.reason ||
            "Choisissez un autre produit avant de poursuivre.",
          icon: mdiAlertCircleOutline,
          canRetry: true,
        };
      }
      if (props.offerCheck) {
        return {
          key: "available",
          label: "Offre Affilizz détectée",
          detail: "Les contenus Affilizz seront créés avec le brouillon RMR.",
          icon: mdiCheckCircleOutline,
        };
      }
      return {
        key: "waiting",
        label: "Recherche Affilizz en attente",
        detail: "Analysez le produit pour rechercher ses offres.",
        icon: mdiClockOutline,
      };
    });

    return { status };
  },
};
</script>

<style lang="scss" scoped>
.ctc-offer-check {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
  padding: 13px 15px;
  border: 1px solid currentColor;
  border-radius: $rmr-radius-xl;
  background: $rmr-surface-0;
  color: $rmr-text-muted;
}

.ctc-offer-check--available {
  border-color: rgba($rmr-green, 0.28);
  background: $rmr-green-bg;
  color: $rmr-green-dark;
}

.ctc-offer-check--warning {
  border-color: $rmr-ca-tint;
  background: $rmr-ca-soft;
  color: $rmr-ca-dark;
}

.ctc-offer-check--error {
  border-color: rgba($rmr-danger, 0.25);
  background: $rmr-red-bg;
  color: $rmr-danger;
}

.ctc-offer-check--loading {
  border-color: $rmr-indigo-border;
  background: $rmr-indigo-bg;
  color: $rmr-indigo;
}

.ctc-offer-check__icon {
  display: flex;
  flex: 0 0 auto;
}

.ctc-offer-check__copy {
  min-width: 0;
  flex: 1 1 auto;
}

.ctc-offer-check__copy strong,
.ctc-offer-check__copy small {
  display: block;
}

.ctc-offer-check__copy strong {
  font-size: $rmr-fs-base;
}

.ctc-offer-check__copy small,
.ctc-offer-check__media {
  margin-top: 2px;
  color: $rmr-text-muted;
  font-size: $rmr-fs-sm;
}

.ctc-offer-check__retry {
  flex: 0 0 auto;
  padding: 5px 8px;
  border: 0;
  border-radius: $rmr-radius-sm;
  background: transparent;
  color: currentColor;
  font-family: inherit;
  font-size: $rmr-fs-sm;
  font-weight: 800;
  text-decoration: underline;
  cursor: pointer;
}

.ctc-offer-check__retry:hover,
.ctc-offer-check__retry:focus-visible {
  background: rgba($rmr-text-primary, 0.06);
  outline: none;
}

.ctc-offer-check__media {
  flex: 0 0 auto;
  margin-top: 0;
  font-weight: 750;
}

.ctc-offer-check--compact {
  display: inline-flex;
  width: auto;
  padding: 7px 10px;
  border-radius: $rmr-radius-pill;
}

.ctc-offer-check--compact .ctc-offer-check__copy {
  flex: 0 1 auto;
}

.ctc-offer-check--compact .ctc-offer-check__copy strong {
  font-size: $rmr-fs-sm;
  white-space: nowrap;
}

@media (max-width: 599px) {
  .ctc-offer-check:not(.ctc-offer-check--compact) {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .ctc-offer-check__media {
    width: 100%;
    padding-left: 29px;
  }
}
</style>
